import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  Row,
  Col,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Tabs,
  Tag,
  Tooltip,
  Empty,
  Popconfirm,
  message,
  Divider,
  Spin,
} from 'antd';
import {
  TeamOutlined,
  UserOutlined,
  CrownOutlined,
  CloseOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { startCase } from 'lodash';
import { useHistory } from 'react-router-dom';
import { useTaskContext } from '../../../../../core/components/tasks/useTaskContext';
import { useUserContext } from '../../../../../core/components/users/useUserContext';
import AvatarS3 from '../../../../shared/images/AvatarS3';
import { space, radius, shadow, text } from '../../configs/designTokens';

const { Option } = Select;

const TeamsTable = ({ createModalVisible, setCreateModalVisible }) => {
  const history = useHistory();
  const {
    currentUser,
    fetchTeams,
    teamsData,
    teamsLoading,
    createTeam,
    createTeamLoading,
    updateTeam,
    updateTeamLoading,
  } = useTaskContext();

  const { fetchBasicUsersDetails, basicUsersDetails, basicUsersDetailsLoading } = useUserContext();

  const [createForm] = Form.useForm();
  const [subscribeForm] = Form.useForm();

  // Create modal visibility is controlled by parent via props

  // Subscribe modal
  const [subscribeModalVisible, setSubscribeModalVisible] = useState(false);
  const [subscribeTeam, setSubscribeTeam] = useState(null);

  const userOrgId = currentUser?.role?.org?.id;

  const refetchTeams = () => {
    if (userOrgId) {
      fetchTeams({
        variables: {
          where: { org: { id: userOrgId } },
          orderBy: 'name_ASC',
        },
      });
    }
  };

  useEffect(() => {
    refetchTeams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userOrgId]);

  const teams = (teamsData?.teams || []).filter((t) => !t.name.startsWith('__deleted__'));

  const fetchUsersForOrg = (name = '') => {
    fetchBasicUsersDetails({
      variables: {
        first: 100,
        skip: 0,
        where: {
          AND: [
            {
              roles_some: {
                org: { id: userOrgId, level: 'PLATFORM' },
                roleItems_some: { feature: 'TASK_MANAGEMENT' },
              },
              name_contains: name,
            },
          ],
        },
      },
    });
  };

  // ============================================
  // CREATE TEAM
  // ============================================

  useEffect(() => {
    if (createModalVisible) {
      createForm.resetFields();
      fetchUsersForOrg();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createModalVisible]);

  const handleCreateSubmit = async () => {
    try {
      const values = await createForm.validateFields();
      const { name, description, leaderId, memberIds } = values;

      // Ensure the leader is always included in members
      const allMemberIds = [
        leaderId,
        ...(memberIds || []).filter((id) => id !== leaderId),
      ];

      const data = {
        name,
        description,
        org: { connect: { id: userOrgId } },
        createdBy: { connect: { id: currentUser.id } },
        leader: { connect: { id: leaderId } },
        members: {
          connect: allMemberIds.map((id) => ({ id })),
        },
      };

      await createTeam({ variables: { data } });
      message.success('Team created successfully');
      setCreateModalVisible(false);
      createForm.resetFields();
      refetchTeams();
    } catch (error) {
      if (error.errorFields) return;
      message.error('Failed to create team: ' + error.message);
    }
  };

  // ============================================
  // SUBSCRIBE / UNSUBSCRIBE
  // ============================================

  // Teams the current user leads (for "subscribe with my team" option)
  const teamsILead = useMemo(() => {
    return teams.filter((t) => t.leader?.id === currentUser?.id);
  }, [teams, currentUser]);

  // For a given target team, figure out which subscribe options are still open:
  //   - canIndividual: I'm not already individually subscribed
  //   - availableSourceTeams: teams I lead that haven't subscribed yet
  const getSubscribeOptions = (targetTeam) => {
    if (!targetTeam) {
      return { canIndividual: true, availableSourceTeams: teamsILead };
    }
    const alreadyIndividual = (targetTeam.subscribers || []).some(
      (s) => s.id === currentUser?.id
    );
    const usedSourceIds = new Set();
    teamsILead.forEach((myT) => {
      if ((myT.subscribedTeams || []).some((st) => st.id === targetTeam.id)) {
        usedSourceIds.add(myT.id);
      }
    });
    return {
      canIndividual: !alreadyIndividual,
      availableSourceTeams: teamsILead.filter((t) => !usedSourceIds.has(t.id)),
    };
  };

  const handleOpenSubscribe = (targetTeam) => {
    setSubscribeTeam(targetTeam);
    subscribeForm.resetFields();
    const opts = getSubscribeOptions(targetTeam);
    const defaultType = opts.canIndividual
      ? 'individual'
      : opts.availableSourceTeams.length > 0
        ? 'team'
        : 'individual';
    subscribeForm.setFieldsValue({ subscriptionType: defaultType });
    setSubscribeModalVisible(true);
  };

  const handleSubscribeSubmit = async () => {
    try {
      const values = await subscribeForm.validateFields();
      const { subscriptionType, sourceTeamId } = values;

      if (subscriptionType === 'individual') {
        // Add current user as individual subscriber to the target team
        await updateTeam({
          variables: {
            data: { subscribers: { connect: [{ id: currentUser.id }] } },
            where: { id: subscribeTeam.id },
          },
        });
        message.success(`You are now subscribed to "${subscribeTeam.name}"`);
      } else {
        // Subscribe source team to the target team
        if (!sourceTeamId) {
          message.warning('Please select which of your teams to subscribe');
          return;
        }
        await updateTeam({
          variables: {
            data: { subscribedTeams: { connect: [{ id: subscribeTeam.id }] } },
            where: { id: sourceTeamId },
          },
        });
        const sourceTeam = teamsILead.find((t) => t.id === sourceTeamId);
        message.success(`Team "${sourceTeam?.name}" is now subscribed to "${subscribeTeam.name}"`);
      }

      setSubscribeModalVisible(false);
      subscribeForm.resetFields();
      refetchTeams();
    } catch (error) {
      if (error.errorFields) return;
      message.error('Failed to subscribe: ' + error.message);
    }
  };

  const handleUnsubscribeIndividual = async (targetTeam) => {
    try {
      await updateTeam({
        variables: {
          data: { subscribers: { disconnect: [{ id: currentUser.id }] } },
          where: { id: targetTeam.id },
        },
      });
      message.success(`Unsubscribed from "${targetTeam.name}"`);
      refetchTeams();
    } catch (error) {
      message.error('Failed to unsubscribe: ' + error.message);
    }
  };

  const handleUnsubscribeTeam = async (targetTeam, sourceTeamId) => {
    try {
      await updateTeam({
        variables: {
          data: { subscribedTeams: { disconnect: [{ id: targetTeam.id }] } },
          where: { id: sourceTeamId },
        },
      });
      message.success(`Team unsubscribed from "${targetTeam.name}"`);
      refetchTeams();
    } catch (error) {
      message.error('Failed to unsubscribe: ' + error.message);
    }
  };

  // ============================================
  // RENDER — Team Cards
  // ============================================

  const userId = currentUser?.id;

  // Categorize teams into:
  //   myTeams:           Teams where I am a member or leader
  //   mySubscribedTeams: Teams I actively subscribed to (individually or via my team)
  //                      NOTE: teams that subscribe TO us do NOT appear here
  //   otherOrgTeams:     Everything else in the org
  const { myTeams, mySubscribedTeams, otherOrgTeams } = useMemo(() => {
    const mine = [];
    const subscribed = [];
    const other = [];

    // 1. Find teams I belong to
    const myTeamIds = new Set();
    teams.forEach((t) => {
      if (t.members?.some((m) => m.id === userId)) {
        myTeamIds.add(t.id);
      }
    });

    // 2. Map each target team -> list of MY teams that subscribe to it.
    //    A target may have multiple sources (e.g. I lead Team A and Team B,
    //    both subscribed to Team Z). subscribedTeams = outgoing follows.
    const subscribedViaTeamMap = {};
    teams.forEach((t) => {
      if (myTeamIds.has(t.id)) {
        (t.subscribedTeams || []).forEach((st) => {
          if (!subscribedViaTeamMap[st.id]) subscribedViaTeamMap[st.id] = [];
          subscribedViaTeamMap[st.id].push(t.id);
        });
      }
    });

    // 3. Categorize each team
    teams.forEach((t) => {
      const isMember = t.members?.some((m) => m.id === userId);
      const isIndividualSubscriber = t.subscribers?.some((s) => s.id === userId);
      const sourceTeamIds = subscribedViaTeamMap[t.id] || [];
      const isSubscribedViaTeam = sourceTeamIds.length > 0 && !isMember;

      if (isMember) {
        mine.push(t);
      } else if (isIndividualSubscriber || isSubscribedViaTeam) {
        subscribed.push({
          ...t,
          _isIndividualSubscriber: isIndividualSubscriber,
          _sourceTeamIds: sourceTeamIds,
        });
      } else {
        other.push(t);
      }
    });

    return { myTeams: mine, mySubscribedTeams: subscribed, otherOrgTeams: other };
  }, [teams, userId]);

  const containerStyle = {
    background: '#fff',
    minHeight: 'calc(100vh - 300px)',
    borderRadius: radius.lg,
    padding: space[4],
  };

  // Reusable card renderer
  const renderTeamCard = (team, options = {}) => {
    const { showIncomingSubscribers, hideSubscribeButton } = options;
    const isIndividualSubscriber = !!team._isIndividualSubscriber;
    const sourceTeamIds = team._sourceTeamIds || [];
    const hasAnySubscription = isIndividualSubscriber || sourceTeamIds.length > 0;
    return (
      <Col key={team.id} xs={24} sm={24} md={12} lg={8}>
        <Card
          hoverable
          style={{
            borderRadius: radius.lg,
            boxShadow: shadow.sm,
            height: '100%',
          }}
          bodyStyle={{ padding: 0 }}
        >
          {/* Card Header */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              padding: `${space[4]}px ${space[5]}px`,
              borderRadius: `${radius.lg}px ${radius.lg}px 0 0`,
              cursor: 'pointer',
              transition: 'background 0.15s ease',
            }}
            className="team-card__header"
            onClick={() => history.push(`/tasks/teams/details/${team.id}`)}
          >
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 16, fontWeight: 600, color: text.primary, marginBottom: 2 }}>
                <TeamOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                {team.name}
                {hasAnySubscription && (
                  <Tag color="blue" style={{ marginLeft: 8, fontSize: 10, verticalAlign: 'middle' }}>
                    Subscribed
                  </Tag>
                )}
              </div>
              <div style={{ fontSize: 13, color: text.secondary, minHeight: 20 }}>
                {team.description || 'No description'}
              </div>
            </div>
          </div>

          {/* Card Body */}
          <div style={{ padding: `0 ${space[5]}px ${space[5]}px` }}>
            <Divider style={{ margin: `0 0 ${space[4]}px 0` }} />

            {/* Leader */}
            <div style={{ marginBottom: space[4] }}>
              <div style={{ fontSize: 12, color: text.label, marginBottom: space[2], textTransform: 'uppercase', letterSpacing: 0.5 }}>
                <CrownOutlined style={{ marginRight: 4, color: '#faad14' }} /> Leader
              </div>
              {team.leader ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <AvatarS3
                    imgKey={team.leader.avatar?.key}
                    username={team.leader.name || team.leader.username}
                    avatarProps={{ size: 24 }}
                  />
                  <span style={{ fontSize: 13, fontWeight: 500 }}>
                    {startCase(team.leader.name || team.leader.username)}
                  </span>
                </div>
              ) : (
                <span style={{ fontSize: 13, color: text.muted }}>No leader assigned</span>
              )}
            </div>

            {/* Members */}
            <div style={{ marginBottom: space[4] }}>
              <div style={{ fontSize: 12, color: text.label, marginBottom: space[2], textTransform: 'uppercase', letterSpacing: 0.5 }}>
                <UserOutlined style={{ marginRight: 4 }} /> Members ({team.members?.length || 0})
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {team.members?.slice(0, 6).map((member) => (
                  <Tooltip key={member.id} title={startCase(member.name || member.username)}>
                    <div className="team-card__avatar">
                      <AvatarS3
                        imgKey={member.avatar?.key}
                        username={member.name || member.username}
                        avatarProps={{ size: 30 }}
                      />
                    </div>
                  </Tooltip>
                ))}
                {team.members?.length > 6 && (
                  <Tag style={{ height: 30, lineHeight: '30px', borderRadius: '50%' }}>
                    +{team.members.length - 6}
                  </Tag>
                )}
                {(!team.members || team.members.length === 0) && (
                  <span style={{ fontSize: 13, color: text.muted }}>No members</span>
                )}
              </div>
            </div>

            {/* Subscriptions — only show "Subscribed to" on non-myTeams tabs */}
            {!showIncomingSubscribers && team.subscribedTeams?.length > 0 && (
              <div style={{ marginBottom: space[3] }}>
                <span style={{ fontSize: 11, color: text.label }}>Subscribed to: </span>
                {team.subscribedTeams.map((t) => (
                  <Tag key={t.id} color="blue" style={{ fontSize: 11 }}>{t.name}</Tag>
                ))}
              </div>
            )}

            {/* Your subscriptions — one tag per source, plus add-another */}
            {hasAnySubscription && (() => {
              const opts = getSubscribeOptions(team);
              const canAddMore = opts.canIndividual || opts.availableSourceTeams.length > 0;
              return (
                <div style={{ marginBottom: space[3] }}>
                  <div style={{ fontSize: 12, color: text.label, marginBottom: space[2], textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Your subscriptions
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                    {isIndividualSubscriber && (
                      <Popconfirm
                        title={`Unsubscribe yourself from "${team.name}"?`}
                        onConfirm={() => handleUnsubscribeIndividual(team)}
                        okText="Yes"
                        cancelText="No"
                      >
                        <Tag color="purple" style={{ cursor: 'pointer', fontSize: 11 }}>
                          <UserOutlined /> You <CloseOutlined style={{ fontSize: 9, marginLeft: 2 }} />
                        </Tag>
                      </Popconfirm>
                    )}
                    {sourceTeamIds.map((srcId) => {
                      const src = teams.find((t) => t.id === srcId);
                      if (!src) return null;
                      return (
                        <Popconfirm
                          key={srcId}
                          title={`Unsubscribe "${src.name}" from "${team.name}"?`}
                          onConfirm={() => handleUnsubscribeTeam(team, srcId)}
                          okText="Yes"
                          cancelText="No"
                        >
                          <Tag color="blue" style={{ cursor: 'pointer', fontSize: 11 }}>
                            Via {src.name} <CloseOutlined style={{ fontSize: 9, marginLeft: 2 }} />
                          </Tag>
                        </Popconfirm>
                      );
                    })}
                    {canAddMore && (
                      <Button
                        size="small"
                        type="dashed"
                        icon={<PlusOutlined />}
                        onClick={() => handleOpenSubscribe(team)}
                        style={{ fontSize: 11, height: 22, padding: '0 8px' }}
                      >
                        Add
                      </Button>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Subscribe Button — only for cards with no existing subscription */}
            {!hideSubscribeButton && !hasAnySubscription && (
              <div style={{ marginTop: space[3], display: 'flex', gap: space[2] }}>
                <Button
                  size="small"
                  style={{
                    background: '#e6f7ff',
                    borderColor: '#91d5ff',
                    color: '#1890ff',
                    fontSize: 12,
                    fontWeight: 500,
                    borderRadius: radius.sm,
                  }}
                  onClick={() => handleOpenSubscribe(team)}
                >
                  <TeamOutlined /> Subscribe
                </Button>
              </div>
            )}
          </div>
        </Card>
      </Col>
    );
  };

  const renderCardGrid = (teamsList, emptyMessage, options = {}) => {
    if (teamsList.length === 0) {
      return (
        <Empty
          description={emptyMessage}
          style={{ padding: space[10] }}
        />
      );
    }
    return (
      <Row gutter={[space[4], space[4]]}>
        {teamsList.map((team) => renderTeamCard(team, {
          showIncomingSubscribers: options.showIncomingSubscribers,
          hideSubscribeButton: options.hideSubscribeButton,
        }))}
      </Row>
    );
  };

  if (teamsLoading && teams.length === 0) {
    return (
      <div style={{ ...containerStyle, textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      {teams.length === 0 ? (
        <Empty
          description="No teams yet. Create your first team to get started."
          style={{ padding: space[10] }}
        />
      ) : (
        <Tabs defaultActiveKey="my" size="small">
          <Tabs.TabPane
            tab={<span>My Teams ({myTeams.length + mySubscribedTeams.length})</span>}
            key="my"
          >
            {/* Own teams — leader or member */}
            {myTeams.length > 0 && (
              <div style={{ marginBottom: space[5] }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: text.primary, marginBottom: space[3] }}>
                  <UserOutlined style={{ marginRight: 6 }} /> Teams I belong to
                </div>
                {renderCardGrid(myTeams, '', { showIncomingSubscribers: true, hideSubscribeButton: true })}
              </div>
            )}

            {/* Teams I subscribed to */}
            {mySubscribedTeams.length > 0 && (
              <div style={{ marginBottom: space[5] }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: text.primary, marginBottom: space[3] }}>
                  <TeamOutlined style={{ marginRight: 6 }} /> Teams I'm subscribed to
                </div>
                {renderCardGrid(mySubscribedTeams, '', { showIncomingSubscribers: true, hideSubscribeButton: true })}
              </div>
            )}

            {myTeams.length === 0 && mySubscribedTeams.length === 0 && (
              <Empty
                description="You are not a member of or subscribed to any teams yet."
                style={{ padding: space[10] }}
              />
            )}
          </Tabs.TabPane>

          <Tabs.TabPane
            tab={<span>Other Teams ({otherOrgTeams.length})</span>}
            key="other"
          >
            {renderCardGrid(otherOrgTeams, 'No other teams in your organization.', { showIncomingSubscribers: true })}
          </Tabs.TabPane>
        </Tabs>
      )}

      {/* ============================================ */}
      {/* CREATE TEAM MODAL — Simple form, auto-leader */}
      {/* ============================================ */}
      <Modal
        title="Create Team"
        visible={createModalVisible}
        onOk={handleCreateSubmit}
        onCancel={() => {
          setCreateModalVisible(false);
          createForm.resetFields();
        }}
        confirmLoading={createTeamLoading}
        width={500}
      >
        <Form form={createForm} layout="vertical">
          <Form.Item
            name="name"
            label="Team Name"
            rules={[
              { required: true, message: 'Please enter a team name' },
              {
                validator: (_, value) => {
                  if (value && teams.some((t) => t.name.toLowerCase() === value.trim().toLowerCase())) {
                    return Promise.reject(new Error('A team with this name already exists'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input placeholder="e.g. Applications, DevOps, Integrations" />
          </Form.Item>

          <Form.Item name="description" label="Description">
            <Input.TextArea placeholder="Brief description of this team" rows={2} />
          </Form.Item>

          <Form.Item
            name="leaderId"
            label="Team Leader"
            rules={[{ required: true, message: 'Please select a team leader' }]}
            initialValue={currentUser.id}
          >
            <Select
              showSearch
              placeholder="Select a team leader"
              loading={basicUsersDetailsLoading}
              onFocus={() => fetchUsersForOrg()}
              onSearch={(searchText) => fetchUsersForOrg(searchText)}
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {basicUsersDetails.map((user) => (
                <Option key={user.id} value={user.id}>
                  {startCase(user.name || user.username)}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="memberIds" label="Members">
            <Select
              mode="multiple"
              allowClear
              showSearch
              placeholder="Select members to add"
              loading={basicUsersDetailsLoading}
              onFocus={() => fetchUsersForOrg()}
              onSearch={(searchText) => fetchUsersForOrg(searchText)}
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {basicUsersDetails
                .filter((user) => user.id !== currentUser.id)
                .map((user) => (
                  <Option key={user.id} value={user.id}>
                    {startCase(user.name || user.username)}
                  </Option>
                ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* ============================================ */}
      {/* SUBSCRIBE MODAL                              */}
      {/* ============================================ */}
      <Modal
        title={`Subscribe to "${subscribeTeam?.name || ''}"`}
        visible={subscribeModalVisible}
        onOk={handleSubscribeSubmit}
        onCancel={() => {
          setSubscribeModalVisible(false);
          subscribeForm.resetFields();
        }}
        confirmLoading={updateTeamLoading}
        width={500}
      >
        <Form form={subscribeForm} layout="vertical">
          {(() => {
            const opts = subscribeTeam
              ? getSubscribeOptions(subscribeTeam)
              : { canIndividual: true, availableSourceTeams: teamsILead };
            return (
              <>
                <Form.Item
                  name="subscriptionType"
                  label="How would you like to subscribe?"
                  rules={[{ required: true, message: 'Please select how to subscribe' }]}
                >
                  <Select
                    onChange={() => subscribeForm.setFieldsValue({ sourceTeamId: undefined })}
                  >
                    <Option value="individual" disabled={!opts.canIndividual}>
                      {opts.canIndividual
                        ? "Just me — I personally get notified about this team's tasks"
                        : "Just me — already subscribed individually"}
                    </Option>
                    <Option value="team" disabled={opts.availableSourceTeams.length === 0}>
                      {opts.availableSourceTeams.length === 0
                        ? 'With my team — all teams I lead are already subscribed'
                        : 'With my team — Subscribe one of my teams to collaborate'}
                    </Option>
                  </Select>
                </Form.Item>

                <Form.Item
                  noStyle
                  shouldUpdate={(prev, cur) => prev.subscriptionType !== cur.subscriptionType}
                >
                  {() => {
                    const type = subscribeForm.getFieldValue('subscriptionType');
                    if (type !== 'team') return null;
                    return (
                      <Form.Item
                        name="sourceTeamId"
                        label="Which of your teams?"
                        rules={[{ required: true, message: 'Please select one of your teams' }]}
                      >
                        <Select
                          showSearch
                          placeholder="Select your team"
                          filterOption={(input, option) =>
                            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                          }
                        >
                          {opts.availableSourceTeams.map((t) => (
                            <Option key={t.id} value={t.id}>
                              {t.name}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    );
                  }}
                </Form.Item>
              </>
            );
          })()}
        </Form>
      </Modal>
    </div>
  );
};

export default TeamsTable;
